import React from 'react';
import LandingMobileView from './LandingMobileView';
import { Box, Typography, Button, makeStyles } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import TopBar from 'src/layouts/MainLayout/TopBar';
import bgImage from '../assets/images/image34.png';
import clockIcon from '../assets/images/clockIcon.png';
import secureIcon from '../assets/images/privacyIcon.png';
import updateIcon from '../assets/images/notificationIcon.png';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const NEW_APP_URL = process.env.REACT_APP_NEW_APP_URL || 'https://tr-claims.nationsafedrivers.com/';

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        // use a CSS variable for consistent vh across browsers
        minHeight: 'calc(var(--vh, 1vh) * 100)',
        fontFamily: 'IBM Plex Sans, Arial, sans-serif',
        backgroundColor: '#fff',
    },
    bgBlur: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        filter: 'grayscale(80%) contrast(85%)',
        opacity: 0.9,
        zIndex: 0,
        pointerEvents: 'none',
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        pointerEvents: 'none',
        background: 'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,0.0) 100%)',
    },
    content: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'flex-start',
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        boxSizing: 'border-box',
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(4),
        paddingTop: 0,
        paddingBottom: theme.spacing(6),
        [theme.breakpoints.up('md')]: {
            paddingLeft: theme.spacing(13),
            paddingRight: theme.spacing(8),
        },
    },
    mainColumn: {
        maxWidth: 881,
        width: '55%',
        marginTop: theme.spacing(10),
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxWidth: '100%',
            paddingTop: theme.spacing(6),
        },
    },
    headline: {
        fontFamily: 'IBM Plex Sans, Arial, sans-serif',
        fontWeight: 700,
        color: '#111',
        margin: 0,
        fontSize: 'clamp(2rem, 6vw, 72px)',
        lineHeight: '1',
        letterSpacing: '-0.04em',
        [theme.breakpoints.up('lg')]: {
            fontSize: '80.16px',
            lineHeight: '80.6px',
            letterSpacing: '-0.04em',
            fontWeight: 700,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '50px',
            lineHeight: '1.05',
            letterSpacing: '-0.02em',
        },
    },
    redText: {
        color: '#D7263D',
        display: 'inline-block',
    },
    subText: {
        marginTop: theme.spacing(2),
        color: '#6b7280',
        fontSize: 'clamp(0.95rem, 1.6vw, 1.125rem)',
        fontFamily: 'IBM Plex Sans, Arial, sans-serif',
        maxWidth: 560,
    },
    claimButton: {
        marginTop: theme.spacing(5),
        backgroundColor: '#D7263D',
        color: '#fff',
        borderRadius: 20,
        height: 80,
        width: 'min(467.61px, 100%)',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 20px 40px rgba(215,38,61,0.16)',
        textTransform: 'none',
        fontFamily: 'IBM Plex Sans, Arial, sans-serif',
        fontWeight: 700,
        fontSize: '1.125rem',
        letterSpacing: '-0.02em',
        '& svg': {
            marginLeft: 12,
            fontSize: '1.25rem',
        },
        '&:hover': {
            backgroundColor: '#c51e2c',
            boxShadow: '0 22px 48px rgba(215,38,61,0.18)',
        },
    },
    featureRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(4),
        marginTop: theme.spacing(4),
        color: '#6b7280',
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        },
    },
    featureGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    featureIconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        borderRadius: '50%',
        padding: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '2px solid #DC2626',
        width: 36,
        height: 36,
    },
    featureIcon: {
        width: 18,
        height: 18,
        display: 'block',
    },
    featureText: {
        color: '#111',
        fontWeight: 500,
        fontSize: '1.2rem',
        fontFamily: 'IBM Plex Sans, Arial, sans-serif',
    },
}));

const LandingRedirect = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    React.useEffect(() => {
        // Set a CSS variable for consistent vh handling across browsers (mobile browser UI)
        const setVh = () => {
            document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
        };
        setVh();
        window.addEventListener('resize', setVh);
        return () => {
            window.removeEventListener('resize', setVh);
        };
    }, []);

    if (isMobile) {
        return <LandingMobileView />;
    }

    const handleRedirect = () => {
        window.location.href = NEW_APP_URL;
    };

    return (
        <Box className={classes.container}>
            <div className={classes.bgBlur} />
            <div className={classes.gradientOverlay} />
            <TopBar />
            <Box className={classes.content}>
                <Box className={classes.mainColumn}>
                    <Typography variant="h1" className={classes.headline}>
                        Experience a<br />seamless<br />
                        <Box component="span" className={classes.redText}>Claims Journey</Box>
                    </Typography>
                    <Typography className={classes.subText}>
                        File, track, and manage your claim with confidence. Real-time updates and 24/7 support at your fingertips.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleRedirect}
                        className={classes.claimButton}
                    >
                        Start Or Access Your Claim <ArrowForwardIcon style={{ fontSize: '1.25rem', marginLeft: 10 }} />
                    </Button>

                </Box>
                <Box className={classes.featureRow}>
                    <Box className={classes.featureGroup}>
                        <Box className={classes.featureIconWrapper}>
                            <img src={clockIcon} alt="24/7 Access" className={classes.featureIcon} />
                        </Box>
                        <Box component="span" className={classes.featureText}>24/7 Access</Box>
                    </Box>

                    <Box className={classes.featureGroup}>
                        <Box className={classes.featureIconWrapper}>
                            <img src={secureIcon} alt="Secure & Private" className={classes.featureIcon} />
                        </Box>
                        <Box component="span" className={classes.featureText}>Secure & Private</Box>
                    </Box>

                    <Box className={classes.featureGroup}>
                        <Box className={classes.featureIconWrapper}>
                            <img src={updateIcon} alt="Instant Updates" className={classes.featureIcon} />
                        </Box>
                        <Box component="span" className={classes.featureText}>Instant Updates</Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LandingRedirect;
